import React, { useState, useEffect } from "react";
import { useAgents } from '../../hooks/useAgents';
import { Agent as AgentDBType } from '../../services/agentsApi';
import { agentsApi } from '../../services/agentsApi';
import { Agent } from '../../types/agentTypes';
import AgentItem from "../Item/AgentItem";
import CreateAgentModal from "../CreateAgentModal";
import { DeleteAgentModal } from "../DeleteAgentModal";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from '../../../../integrations/supabase/client';

type Props = {
    agents?: Agent[];
};

const AVATAR_COLORS = [
    { from: '#6366F1', to: '#8B5CF6' }, // indigo → violet
    { from: '#3B82F6', to: '#06B6D4' }, // blue → cyan
    { from: '#10B981', to: '#34D399' }, // emerald → green
    { from: '#F59E0B', to: '#F97316' }, // amber → orange
    { from: '#EF4444', to: '#F43F5E' }, // red → rose
    { from: '#EC4899', to: '#A855F7' }, // pink → purple
    { from: '#14B8A6', to: '#06B6D4' }, // teal → cyan
    { from: '#8B5CF6', to: '#EC4899' }, // violet → pink
    { from: '#F97316', to: '#EF4444' }, // orange → red
    { from: '#06B6D4', to: '#3B82F6' }, // cyan → blue
    { from: '#84CC16', to: '#10B981' }, // lime → emerald
    { from: '#D946EF', to: '#6366F1' }, // fuchsia → indigo
];

const AgentList: React.FC<Props> = ({ agents: propAgents = [] }) => {
    const { agents: hookAgents, reload } = useAgents();
    const [phoneMap, setPhoneMap] = useState<Record<string, string>>({});

    // Fetch phone numbers from agent_channels
    useEffect(() => {
        const fetchPhones = async () => {
            const agentIds = hookAgents.map(a => a.id);
            if (agentIds.length === 0) return;

            const { data } = await supabase
                .from('agent_channels')
                .select('agent_id, numero_telefono, is_primary')
                .in('agent_id', agentIds)
                .order('is_primary', { ascending: false });

            if (data) {
                const map: Record<string, string> = {};
                data.forEach(ch => {
                    // Keep first (primary) phone per agent
                    if (!map[ch.agent_id]) {
                        map[ch.agent_id] = ch.numero_telefono;
                    }
                });
                setPhoneMap(map);
            }
        };
        fetchPhones();
    }, [hookAgents]);

    // Convertir agentes de DB al formato de UI
    const dbAgentsAsUIAgents: Agent[] = hookAgents.map((dbAgent, index) => ({
        id: dbAgent.id,
        name: dbAgent.nombre,
        description: dbAgent.descripcion || undefined,
        status: dbAgent.is_active ? 'active' : 'inactive',
        funnel: undefined,
        plan: dbAgent.plan ?? undefined,
        phone: phoneMap[dbAgent.id] || undefined,
        profileImage: undefined,
        _colorIndex: index % AVATAR_COLORS.length,
    }));

    const agents = propAgents.length > 0 ? propAgents : dbAgentsAsUIAgents;
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [agentToDelete, setAgentToDelete] = useState<Agent | AgentDBType | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleNewAgentClick = () => {
        setShowCreateModal(true);
    };

    const handleDeleteClick = (agent: Agent | AgentDBType) => {
        console.log('[AgentList] 🗑️ Iniciando proceso de eliminación del agente:', agent.id);
        setAgentToDelete(agent);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!agentToDelete) return;

        setDeleting(true);
        try {
            console.log('[AgentList] 🔄 Eliminando agente:', agentToDelete.id);
            await agentsApi.deleteAgent(agentToDelete.id);
            console.log('[AgentList] ✅ Agente eliminado correctamente');
            toast.success('Agente eliminado correctamente');
            setShowDeleteModal(false);
            setAgentToDelete(null);
            reload(); // Recargar lista sin full page reload
        } catch (error) {
            console.error('[AgentList] ❌ Error eliminando agente:', error);
            toast.error('Error al eliminar el agente');
        } finally {
            setDeleting(false);
        }
    };

    const handleCloseDeleteModal = () => {
        if (!deleting) {
            setShowDeleteModal(false);
            setAgentToDelete(null);
        }
    };

    return (
        <div className="agents-page-container flex flex-col w-full h-full">
            {/* Header Section */}
            <div className="agents-header flex items-center justify-between mb-8 px-6 pt-6">
                <h1 className="text-foreground font-bold text-[23px] leading-[28px] font-montserrat">
                    Mis Agentes
                </h1>

                {/* Search and Button Section */}
                <div className="flex items-center gap-2">
                    <div className="relative w-[394px]">
                        <input
                            type="text"
                            placeholder="Buscar"
                            className="w-full h-[42px] pl-[20px] pr-10 rounded-[10px] border border-border bg-background text-foreground/60 font-montserrat text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                    <button
                        onClick={handleNewAgentClick}
                        className="flex items-center justify-center gap-2 h-[42px] px-6 rounded-[10px] bg-primary text-black font-montserrat text-sm hover:bg-primary/90 transition-colors"
                    >
                        <Plus size={20} />
                        Nuevo Agente
                    </button>
                </div>
            </div>

            {/* Agents Grid */}
            <div className="agents-grid-container flex-1 px-6 pb-6">
                {agents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {agents.map((agent, index) => (
                            <AgentItem
                                key={agent.id}
                                agent={agent}
                                colorIndex={agent._colorIndex ?? index % AVATAR_COLORS.length}
                                avatarColors={AVATAR_COLORS}
                                onDelete={() => handleDeleteClick(agent)}
                                onNameUpdated={() => reload()}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-foreground/60 text-center">
                            No hay agentes disponibles. Crea tu primer agente.
                        </p>
                    </div>
                )}
            </div>

            <CreateAgentModal 
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={() => {
                    console.log('[AgentList] ✅ Agente creado, recargando lista');
                    setShowCreateModal(false);
                    reload(); // Recargar lista sin full page reload
                }}
            />

            <DeleteAgentModal
                open={showDeleteModal}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                agentName={(agentToDelete as any)?.name || (agentToDelete as any)?.nombre || ''}
                loading={deleting}
            />
        </div>
    );
};

export default AgentList;
